import { useQueryClient } from '@tanstack/react-query';
import { getMessagesFromStorage, saveMessagesToStorage, generateId, getMessageFindQueryKey, getMessageFindPrefix, } from '../../message/local-storage/localStorageMessageRepository';
const STORAGE_KEY = 'varos_threads';
const MAX_THREADS = 50;
// Helper functions for sessionStorage operations
function getThreadsFromStorage() {
    try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        if (!stored)
            return [];
        const parsed = JSON.parse(stored);
        // Parse and validate threads, converting date strings back to Date objects
        const validatedThreads = [];
        parsed.forEach((thread) => {
            try {
                validatedThreads.push({
                    id: thread.id,
                    object: thread.object,
                    title: thread.title,
                    metadata: thread.metadata,
                    context: thread.context,
                    createdAt: new Date(thread.createdAt),
                });
            }
            catch (error) {
                console.warn(`Failed to parse thread ${thread.id}:`, error);
            }
        });
        return validatedThreads;
    }
    catch (error) {
        console.warn('Failed to parse stored threads:', error);
        return [];
    }
}
function saveThreadsToStorage(threads) {
    try {
        // Enforce maximum number of threads by keeping only the most recent ones
        let limitedThreads = threads;
        if (threads.length > MAX_THREADS) {
            // Sort by createdAt descending (newest first) and take the first MAX_THREADS
            limitedThreads = threads
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .slice(0, MAX_THREADS);
        }
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(limitedThreads));
    }
    catch (error) {
        console.error('Error saving threads to sessionStorage:', error);
    }
}
function generateThreadId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
}
// Shared thread query key functions
export function getThreadFindQueryKey(props) {
    return ['platform', 'thread', 'find', props];
}
export function getThreadLookupQueryKey(props) {
    return ['platform', 'thread', 'lookup', props.id];
}
export function getThreadFindPrefix() {
    return ['platform', 'thread', 'find'];
}
export function createLocalStorageThreadRepository(config) {
    const { repository } = config;
    return {
        useFind(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getThreadFindQueryKey(props),
                async queryFn() {
                    const allThreads = getThreadsFromStorage();
                    // Apply limit if specified
                    let filteredThreads = allThreads;
                    if (props.limit) {
                        // Sort by createdAt descending (newest first) and take the first props.limit
                        filteredThreads = filteredThreads
                            .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
                            .slice(0, props.limit);
                    }
                    else {
                        // Sort by createdAt descending for newest first
                        filteredThreads = filteredThreads.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
                    }
                    return filteredThreads;
                },
            });
            return query;
        },
        useLookup(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getThreadLookupQueryKey(props),
                async queryFn() {
                    const allThreads = getThreadsFromStorage();
                    const thread = allThreads.find((t) => t.id === props.id);
                    if (!thread) {
                        throw new Error(`Thread with id ${props.id} not found`);
                    }
                    return thread;
                },
            });
            return query;
        },
        useCreate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const allThreads = getThreadsFromStorage();
                    const allMessages = getMessagesFromStorage();
                    const newThread = {
                        id: generateThreadId(),
                        object: 'thread',
                        title: props.title || 'New Thread',
                        metadata: props.metadata || {},
                        context: props.context || [],
                        createdAt: new Date(),
                    };
                    // Create messages if provided
                    const newMessages = [];
                    if (props.messages) {
                        console.log('ThreadRepository: Creating messages for new thread:', props.messages);
                        props.messages.forEach((messageData) => {
                            const newMessage = {
                                id: generateId(),
                                thread: newThread.id,
                                role: messageData.role || 'user',
                                parts: messageData.parts,
                                context: messageData.context || [],
                                createdAt: new Date(),
                            };
                            console.log('ThreadRepository: Created message:', {
                                id: newMessage.id,
                                thread: newMessage.thread,
                                role: newMessage.role,
                                content: newMessage.parts
                                    .map((p) => (p.type === 'text' ? p.text : ''))
                                    .join(' '),
                            });
                            newMessages.push(newMessage);
                        });
                    }
                    // Save both thread and messages atomically
                    const updatedThreads = [...allThreads, newThread];
                    const updatedMessages = [...allMessages, ...newMessages];
                    console.log('ThreadRepository: Saving thread and messages:', {
                        threadId: newThread.id,
                        messageCount: newMessages.length,
                        totalThreadsAfter: updatedThreads.length,
                        totalMessagesAfter: updatedMessages.length,
                    });
                    saveThreadsToStorage(updatedThreads);
                    saveMessagesToStorage(updatedMessages);
                    queryClient.setQueryData(getMessageFindQueryKey({
                        thread: newThread.id,
                    }), updatedMessages);
                    return newThread;
                },
                onMutate: async (props) => {
                    // Cancel any outgoing refetches to prevent race conditions
                    await queryClient.cancelQueries({
                        queryKey: getThreadFindPrefix(),
                    });
                    await queryClient.cancelQueries({
                        queryKey: getMessageFindPrefix(),
                    });
                    // Generate consistent IDs for optimistic updates
                    const newThreadId = generateThreadId();
                    // Snapshot the previous values for rollback
                    const previousThreads = queryClient.getQueryData(getThreadFindQueryKey({}));
                    // Optimistically update with new thread
                    const newThread = {
                        id: newThreadId,
                        object: 'thread',
                        title: props.title || 'New Thread',
                        metadata: props.metadata || {},
                        context: props.context || [],
                        createdAt: new Date(),
                    };
                    queryClient.setQueryData(getThreadFindQueryKey({}), (old) => {
                        const currentThreads = old || [];
                        return [newThread, ...currentThreads]; // Add to beginning for newest first
                    });
                    // Optimistically update with new messages if provided
                    if (props.messages) {
                        const newMessages = props.messages.map((messageData) => ({
                            id: generateId(),
                            thread: newThreadId,
                            role: messageData.role || 'user',
                            parts: messageData.parts,
                            context: messageData.context || [],
                            createdAt: new Date(),
                        }));
                        // Update message queries for the new thread
                        const messageQueryKey = getMessageFindQueryKey({
                            thread: newThreadId,
                        });
                        queryClient.setQueryData(messageQueryKey, newMessages);
                    }
                    return { previousThreads, newThread };
                },
                onError: (err, newThread, context) => {
                    // Rollback on error
                    if (context?.previousThreads) {
                        queryClient.setQueryData(getThreadFindQueryKey({}), context.previousThreads);
                    }
                },
                async onSuccess(data, variables) {
                    console.log('ThreadRepository: onSuccess called for thread creation:', {
                        threadId: data.id,
                        hadMessages: !!variables.messages,
                        messageCount: variables.messages?.length || 0,
                    });
                    // Aggressively invalidate and refetch all related queries
                    const invalidationPromises = [
                        // Invalidate all thread queries
                        queryClient.invalidateQueries({
                            queryKey: getThreadFindPrefix(),
                        }),
                        // Invalidate all message queries
                        queryClient.invalidateQueries({
                            queryKey: getMessageFindPrefix(),
                        }),
                        // Invalidate specific thread lookup
                        queryClient.invalidateQueries({
                            queryKey: getThreadLookupQueryKey({ id: data.id }),
                        }),
                    ];
                    // If messages were created, also invalidate specific message queries
                    if (variables.messages) {
                        console.log('ThreadRepository: Invalidating message queries for thread:', data.id);
                        invalidationPromises.push(queryClient.invalidateQueries({
                            queryKey: getMessageFindQueryKey({ thread: data.id }),
                        }));
                    }
                    await Promise.all(invalidationPromises);
                    console.log('ThreadRepository: Cache invalidation completed');
                    // Force immediate refetch of critical queries to ensure cache consistency
                    const refetchPromises = [
                        queryClient.refetchQueries({
                            queryKey: getThreadFindQueryKey({}),
                        }),
                    ];
                    // Always refetch messages for the new thread if they were created
                    if (variables.messages) {
                        console.log('ThreadRepository: Force refetching messages for thread:', data.id);
                        refetchPromises.push(queryClient.refetchQueries({
                            queryKey: getMessageFindQueryKey({ thread: data.id }),
                        }));
                    }
                    await Promise.all(refetchPromises);
                    console.log('ThreadRepository: Cache refetch completed');
                },
            });
        },
        useUpdate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const [threadId, payload] = props;
                    const allThreads = getThreadsFromStorage();
                    const threadIndex = allThreads.findIndex((t) => t.id === threadId);
                    if (threadIndex === -1) {
                        throw new Error(`Thread with id ${threadId} not found. available threads: ${allThreads.map((t) => t.id).join(', ')}`);
                    }
                    const existingThread = allThreads[threadIndex];
                    const updatedThread = {
                        ...existingThread,
                        context: payload.context ?? existingThread.context,
                        title: payload.title ?? existingThread.title,
                        metadata: payload.metadata ?? existingThread.metadata,
                    };
                    const updatedThreads = [...allThreads];
                    updatedThreads[threadIndex] = updatedThread;
                    saveThreadsToStorage(updatedThreads);
                    return updatedThread;
                },
                async onSuccess(data) {
                    await queryClient.refetchQueries({
                        queryKey: ['platform', 'thread'],
                    });
                },
            });
        },
        useGenerate(context) {
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const response = await config.client.user.threadGenerate({
                        xWorkspaceId: context.workspace.id,
                        threadGenerateRequest: props,
                    });
                    return response.data;
                },
            });
        },
    };
}
//# sourceMappingURL=localStorageThreadRepository.js.map